import type { JSX } from 'solid-js'
import { Portal } from 'solid-js/web'

type ModalProps = {
   id?: string
   children: JSX.Element
   /** Called when the user clicks the backdrop outside the panel. */
   onDismiss: () => void
}

/**
 * Renders a centered modal: a portal with a blurred backdrop, a raised panel,
 * and dismiss-on-backdrop-click. Mount conditionally with `<Show>` at the
 * call site.
 *
 * @param props - The modal props.
 * @returns The modal element.
 */
const Modal = (props: ModalProps) => {
   let panelRef: HTMLDivElement | undefined
   let dismissArmed = false

   return (
      <Portal>
         <div
            id={props.id}
            class="fixed inset-0 z-50 flex items-center justify-center bg-surface-sunken/75 p-4 backdrop-blur-sm"
            // Native listener so it runs before document-level outside-press
            // handlers close an open dropdown: that press only closes the dropdown.
            on:pointerdown={event => {
               dismissArmed =
                  event.target === event.currentTarget &&
                  !panelRef?.querySelector('[aria-expanded="true"]')
            }}
            onClick={event => {
               if (dismissArmed && event.target === event.currentTarget) {
                  props.onDismiss()
               }
               dismissArmed = false
            }}
         >
            <div
               ref={panelRef}
               class="edit-modal-content w-full max-w-sm space-y-4 rounded-card border border-edge bg-surface-raised p-6 text-ink shadow-2xl"
            >
               {props.children}
            </div>
         </div>
      </Portal>
   )
}

export default Modal
